import { SkeletonLoading } from '@saleshandy/design-system';
import React from 'react';
import Table from '../../../../shared/design-system/components/organisms/table';
import { PaginationShowHide } from '../../../../shared/design-system/components/organisms/table/types';

const EmailAccountsFallback: React.FC = () => {
  const columns = [
    {
      dataField: 'status',
      text: '',
      headerAlign: 'start',
      headerClasses: 'status-header',
      headerLoadingSkeleton: <SkeletonLoading width={80} height={14} />,
      cellLoadingSkeleton: <SkeletonLoading width={180} height={14} />,
    },
    {
      dataField: 'fromName',
      text: 'Sender Name',
      headerAlign: 'start',
      headerClasses: 'name-header',
      cellClasses: 'name-cell',
      headerLoadingSkeleton: <SkeletonLoading width={80} height={14} />,
      cellLoadingSkeleton: <SkeletonLoading width={180} height={14} />,
    },
    {
      dataField: 'activeSequences',
      text: 'Active Sequences',
      headerAlign: 'start',
      headerClasses: 'active-sequence-cell-header',
      cellClasses: 'active-sequences-cell active-sequence-cell-header',
      headerLoadingSkeleton: <SkeletonLoading width={62} height={14} />,
      cellLoadingSkeleton: (
        <SkeletonLoading width={32} height={24} borderRadius={20} />
      ),
    },
    {
      dataField: 'warmupStatus',
      text: 'Warm-up Status',
      headerAlign: 'start',
      cellClasses: 'warmup-status-cell',
      headerLoadingSkeleton: <SkeletonLoading width={62} height={14} />,
      cellLoadingSkeleton: (
        <SkeletonLoading width={32} height={24} borderRadius={20} />
      ),
    },
    {
      dataField: 'emailWarmupScore',
      text: 'Outreach Readiness',
      headerAlign: 'start',
      cellClasses: 'warmup-status-cell',
      headerLoadingSkeleton: <SkeletonLoading width={62} height={14} />,
      cellLoadingSkeleton: (
        <SkeletonLoading width={32} height={24} borderRadius={20} />
      ),
    },
    {
      dataField: 'deliverabilityRate',
      text: 'Deliverability Score',
      headerAlign: 'start',
      cellClasses: 'deliverability-score-cell',
      headerLoadingSkeleton: <SkeletonLoading width={62} height={14} />,
      cellLoadingSkeleton: (
        <SkeletonLoading width={32} height={24} borderRadius={20} />
      ),
    },
    {
      dataField: 'healthScore',
      text: 'Setup Score',
      headerAlign: 'start',
      cellClasses: 'setup-score-cell',
      headerLoadingSkeleton: <SkeletonLoading width={62} height={14} />,
      cellLoadingSkeleton: (
        <SkeletonLoading width={32} height={24} borderRadius={20} />
      ),
    },
    {
      dataField: 'spf',
      text: 'SPF',
      headerAlign: 'start',
      headerLoadingSkeleton: <SkeletonLoading width={62} height={14} />,
      cellLoadingSkeleton: (
        <SkeletonLoading width={32} height={24} borderRadius={20} />
      ),
    },
    {
      dataField: 'dkim',
      text: 'DKIM',
      headerAlign: 'start',
      headerLoadingSkeleton: <SkeletonLoading width={62} height={14} />,
      cellLoadingSkeleton: (
        <SkeletonLoading width={32} height={24} borderRadius={20} />
      ),
    },
    {
      dataField: 'dmarc',
      text: 'DMARC',
      headerAlign: 'start',
      headerLoadingSkeleton: <SkeletonLoading width={62} height={14} />,
      cellLoadingSkeleton: (
        <SkeletonLoading width={32} height={24} borderRadius={20} />
      ),
    },
    {
      dataField: 'domainBlacklist',
      text: 'Domain Blacklist',
      headerAlign: 'start',
      headerLoadingSkeleton: <SkeletonLoading width={62} height={14} />,
      cellLoadingSkeleton: (
        <SkeletonLoading width={32} height={24} borderRadius={20} />
      ),
    },
    {
      dataField: 'ipBlacklist',
      text: 'IP Blacklist',
      headerAlign: 'start',
      headerLoadingSkeleton: <SkeletonLoading width={62} height={14} />,
      cellLoadingSkeleton: (
        <SkeletonLoading width={32} height={24} borderRadius={20} />
      ),
    },
    {
      dataField: 'mxRecord',
      text: 'MX Record',
      headerAlign: 'start',
      headerLoadingSkeleton: <SkeletonLoading width={62} height={14} />,
      cellLoadingSkeleton: (
        <SkeletonLoading width={32} height={24} borderRadius={20} />
      ),
    },
    {
      dataField: 'customTrackingDomain',
      text: 'Custom Domain',
      headerAlign: 'start',
      headerLoadingSkeleton: <SkeletonLoading width={62} height={14} />,
      cellLoadingSkeleton: (
        <SkeletonLoading width={32} height={24} borderRadius={20} />
      ),
    },
    {
      dataField: 'tags',
      text: 'Tags',
      headerAlign: 'left',
      cellClasses: 'tag-detail',
      style: () => ({ width: '5rem' }),
      headerLoadingSkeleton: <SkeletonLoading width={29} height={14} />,
      cellLoadingSkeleton: (
        <div className="d-flex">
          <SkeletonLoading width={84} height={24} />
          <div className="ml-2">
            <SkeletonLoading width={64} height={24} />
          </div>
        </div>
      ),
    },
    {
      dataField: 'emailSent',
      text: 'Email Sent',
      headerAlign: 'start',
      cellClasses: 'email-sent-cell',
      headerLoadingSkeleton: <SkeletonLoading width={62} height={14} />,
      cellLoadingSkeleton: (
        <SkeletonLoading width={32} height={24} borderRadius={20} />
      ),
    },
    {
      dataField: 'client',
      text: 'Client Associated',
      headerAlign: 'start',
      cellClasses: 'email-account-client-associated',
      headerLoadingSkeleton: <SkeletonLoading width={68} height={14} />,
      cellLoadingSkeleton: <SkeletonLoading width={76} height={14} />,
    },
    {
      dataField: 'user',
      text: 'Added By',
      headerAlign: 'start',
      cellClasses: 'added-by-cell',
      headerLoadingSkeleton: <SkeletonLoading width={62} height={14} />,
      cellLoadingSkeleton: (
        <SkeletonLoading width={32} height={24} borderRadius={20} />
      ),
    },
  ];

  return (
    <div className="email-account-container">
      <div className="email-accounts-actions">
        <div className="d-flex align-items-center">
          <SkeletonLoading width={206} height={32} />
          <div className="icon-container position-relative">
            <div className="d-flex align-items-center justify-content-center icon">
              <SkeletonLoading width={24} height={24} />
            </div>
          </div>
          <div className="separator-line" />
          <div className="d-flex align-items-center justify-content-center email-account-icons">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                className="icon d-flex align-items-center justify-content-center"
                // eslint-disable-next-line react/no-array-index-key
                key={i}
              >
                <SkeletonLoading width={24} height={24} />
              </div>
            ))}
          </div>
        </div>
        <div className="d-flex gap-16 justify-content-center align-items-center">
          <SkeletonLoading width={158} height={32} />
        </div>
      </div>
      <div className="email-accounts-table">
        <Table
          data={[]}
          columns={columns}
          isLoading={true}
          pagination={PaginationShowHide.HIDE}
          paginationOptions={{
            options: {
              page: 1,
              limit: 10,
              totalElements: 10,
            },
          }}
        />
      </div>
    </div>
  );
};

export default EmailAccountsFallback;
